import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDocs,
  query,
  where,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { BILLING_CODES } from '../models/BillingCode';

/**
 * A billing (CPT) code managed at runtime in Firestore, per company. Replaces the
 * hardcoded BILLING_CODES const for management/administration — admins can add new
 * codes and deactivate ones they don't use. Seeded from the const the first time.
 */
export interface RuntimeBillingCode {
  id: string;
  companyId: string;
  code: string;
  description: string;
  typicalDuration: number;      // minutes
  requiresSupervision: boolean;
  allowsTelehealth: boolean;
  active: boolean;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface NewBillingCodeInput {
  code: string;
  description: string;
  typicalDuration?: number;
  requiresSupervision?: boolean;
  allowsTelehealth?: boolean;
}

class BillingCodeService {
  private col = collection(db, 'billingCodes');

  /** All billing codes for a company, sorted by code (in memory — no index). */
  async getByCompany(companyId: string): Promise<RuntimeBillingCode[]> {
    const q = query(this.col, where('companyId', '==', companyId));
    const snap = await getDocs(q);
    return snap.docs
      .map((d) => ({ id: d.id, ...d.data() } as RuntimeBillingCode))
      .sort((a, b) => a.code.localeCompare(b.code));
  }

  /** Active codes only (for pickers). */
  async getActiveByCompany(companyId: string): Promise<RuntimeBillingCode[]> {
    return (await this.getByCompany(companyId)).filter((c) => c.active);
  }

  async add(companyId: string, input: NewBillingCodeInput): Promise<string> {
    const now = Timestamp.now();
    const ref = await addDoc(this.col, {
      companyId,
      code: input.code.trim(),
      description: input.description.trim(),
      typicalDuration: input.typicalDuration ?? 60,
      requiresSupervision: input.requiresSupervision ?? false,
      allowsTelehealth: input.allowsTelehealth ?? true,
      active: true,
      createdAt: now,
      updatedAt: now,
    });
    return ref.id;
  }

  async setActive(id: string, active: boolean): Promise<void> {
    await updateDoc(doc(this.col, id), { active, updatedAt: Timestamp.now() });
  }

  /**
   * Seed a company's billing codes from the standard const the first time (no-op
   * if any already exist). Returns the number seeded.
   */
  async seedDefaults(companyId: string): Promise<number> {
    const existing = await this.getByCompany(companyId);
    if (existing.length > 0) return 0;
    const now = Timestamp.now();
    const codes = Object.values(BILLING_CODES);
    await Promise.all(
      codes.map((c) =>
        addDoc(this.col, {
          companyId,
          code: c.code,
          description: c.description,
          typicalDuration: c.typicalDuration,
          requiresSupervision: c.requiresSupervision,
          allowsTelehealth: c.allowsTelehealth,
          active: true,
          createdAt: now,
          updatedAt: now,
        })
      )
    );
    return codes.length;
  }
}

export const billingCodeService = new BillingCodeService();
